// api/tacho.js

/**
 * API functions for Tacho Infringement Management
 * 
 * Handles communication between frontend and backend for:
 * - Driver retrieval
 * - Infringement CRUD operations
 * - File uploads and downloads
 * - Status updates
 */

import { getSessionUUID } from '../components/lib/AuthToken';
import { authFetch } from '../providers/AuthProvider';
import logger from '../utils/logger';
import JSZip from 'jszip';

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
};

/**
 * Get all drivers and admin users for infringement assignment
 */
export const getDrivers = async () => {
    try {
        logger.log('🚛 Fetching drivers for tacho infringement assignment...');
        
        const response = await authFetch('/api/tacho/drivers');
        const data = await handleResponse(response);
        logger.log('✅ Drivers fetched successfully:', data.drivers);
        
        return {
            success: true,
            drivers: data.drivers || []
        };
    } catch (error) {
        logger.error('❌ Error fetching drivers:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get all tacho infringements
 */

export const getInfringements = async () => {
    try {
        logger.log('📋 Fetching tacho infringements...');
        
        const response = await authFetch('/api/tacho/infringements');
        const data = await handleResponse(response);
        logger.log('✅ Infringements fetched successfully:', data.infringements);
        
        return {
            success: true,
            infringements: data.infringements || [],
            count: data.count || 0
        };
    } catch (error) {
        logger.error('❌ Error fetching infringements:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get the latest 50 infringements for the transport audit selector.
 */
export const getAuditInfringements = async () => {
    try {
        logger.log('📋 Fetching recent tacho infringements for audit...');

        const response = await authFetch('/api/tacho/infringements?audit_selection=true');
        const data = await handleResponse(response);

        return {
            success: true,
            infringements: data.infringements || [],
            count: data.count || 0
        };
    } catch (error) {
        logger.error('❌ Error fetching audit infringements:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get tacho infringements for a specific driver
 */
export const getDriverInfringements = async (driverId) => {
    try {
        logger.log('🚛 Fetching driver infringements for driver ID:', driverId);
        
        const response = await authFetch(`/api/tacho/infringements?driver_id=${driverId}`);
        const data = await handleResponse(response);
        logger.log('✅ Driver infringements fetched successfully:', data.infringements);
        
        return {
            success: true,
            infringements: data.infringements || [],
            count: data.count || 0
        };
    } catch (error) {
        logger.error('❌ Error fetching driver infringements:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Create a new tacho infringement
 */
export const createInfringement = async (infringementData, pdfFile = null, printoutImage = null) => {
    try {
        logger.log('📝 Creating new tacho infringement...', infringementData);

        const formData = new FormData();

        // Add infringement data to form
        Object.keys(infringementData).forEach(key => {
            if (infringementData[key] !== null && infringementData[key] !== undefined && infringementData[key] !== '') {
                formData.append(key, infringementData[key]);
            }
        });

        // Add files if present
        if (pdfFile) {
            formData.append('pdf_file', pdfFile);
        }

        if (printoutImage) {
            formData.append('printout_image', printoutImage);
        }

        // Use custom authFetch for FormData
        const sessionUUID = getSessionUUID();
        const response = await fetch('/api/tacho/infringements', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionUUID}`
                // Note: Don't set Content-Type for FormData, let browser set it with boundary
            },
            body: formData
        });

        const data = await handleResponse(response);
        logger.log('✅ Infringement created successfully:', data.infringement);
        
        return {
            success: true,
            infringement: data.infringement,
            message: data.message
        };
    } catch (error) {
        logger.error('❌ Error creating infringement:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get detailed information about a specific infringement
 */
export const getInfringementDetails = async (infringementId) => {
    try {
        logger.log(`🔍 Fetching infringement details for ID: ${infringementId}`);
        
        const response = await authFetch(`/api/tacho/infringements/${infringementId}`);
        const data = await handleResponse(response);
        logger.log('✅ Infringement details fetched successfully:', data.infringement);
        
        return {
            success: true,
            infringement: data.infringement
        };
    } catch (error) {
        logger.error('❌ Error fetching infringement details:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Update infringement status (for mobile sign-off)
 */
export const updateInfringementStatus = async (infringementId, status, signatureData = null, acknowledgedBy = null) => {
    try {
        logger.log(`📝 Updating infringement ${infringementId} status to: ${status}`);
        
        const requestData = {
            status,
            acknowledged_by: acknowledgedBy
        };

        if (signatureData) {
            requestData.signature_data = signatureData;
        }

        const response = await authFetch(`/api/tacho/infringements/${infringementId}/status`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        });

        const data = await handleResponse(response);
        logger.log('✅ Infringement status updated successfully:', data.infringement);
        
        return {
            success: true,
            infringement: data.infringement,
            message: data.message
        };
    } catch (error) {
        logger.error('❌ Error updating infringement status:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Update an existing infringement
 */
export const updateInfringement = async (infringementId, updateData) => {
    try {
        logger.log(`📝 Updating infringement ${infringementId}...`, updateData);
        
        const response = await authFetch(`/api/tacho/infringements/${infringementId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
        });

        const data = await handleResponse(response);
        logger.log('✅ Infringement updated successfully:', data.infringement);
        
        return {
            success: true,
            infringement: data.infringement,
            message: data.message
        };
    } catch (error) {
        logger.error('❌ Error updating infringement:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get file URL for an infringement (PDF or printout image)
 */
export const getInfringementFile = async (infringementId, fileType) => {
    try {
        logger.log(`📄 Getting ${fileType} file for infringement ${infringementId}`);
        
        const response = await authFetch(`/api/tacho/infringements/${infringementId}/files/${fileType}`);

        const data = await handleResponse(response);
        logger.log('✅ File URL retrieved successfully');
        
        return {
            success: true,
            file_url: data.file_url
        };
    } catch (error) {
        logger.error('❌ Error getting infringement file:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Delete an infringement (admin only)
 */
export const deleteInfringement = async (infringementId) => {
    try {
        logger.log(`🗑️ Deleting infringement ${infringementId}`);
        
        const response = await authFetch(`/api/tacho/infringements/${infringementId}`, {
            method: 'DELETE'
        });

        const data = await handleResponse(response);
        logger.log('✅ Infringement deleted successfully');
        
        return {
            success: true,
            message: data.message,
            files_deleted: data.files_deleted
        };
    } catch (error) {
        logger.error('❌ Error deleting infringement:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get infringement statistics
 */
export const getInfringementStats = async () => {
    try {
        logger.log('📊 Fetching infringement statistics...');
        
        const response = await authFetch('/api/tacho/stats');

        const data = await handleResponse(response);
        logger.log('✅ Statistics fetched successfully:', data.stats);
        
        return {
            success: true,
            stats: data.stats
        };
    } catch (error) {
        logger.error('❌ Error fetching statistics:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Download infringement file
 */
export const downloadInfringementFile = async (infringementId, fileType, fileName) => {
    try {
        logger.log(`💾 Downloading ${fileType} file for infringement ${infringementId}`);
        
        const fileResponse = await getInfringementFile(infringementId, fileType);
        
        if (!fileResponse.success) {
            throw new Error(fileResponse.error);
        }

        // Create a temporary link and click it to trigger download
        const link = document.createElement('a');
        link.href = fileResponse.file_url;
        link.download = fileName || `infringement_${infringementId}_${fileType}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        logger.log('✅ File download initiated');
        
        return {
            success: true,
            message: 'Download initiated'
        };
    } catch (error) {
        logger.error('❌ Error downloading file:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Filter infringements by status
 */
export const filterInfringementsByStatus = (infringements, status) => {
    if (!status || status === 'all') {
        return infringements;
    }
    
    return infringements.filter(infringement => infringement.status === status);
};

/**
 * Filter infringements by severity
 */
export const filterInfringementsBySeverity = (infringements, severity) => {
    if (!severity || severity === 'all') {
        return infringements;
    }
    
    return infringements.filter(infringement => infringement.severity === severity);
};

/**
 * Filter infringements by driver
 */
export const filterInfringementsByDriver = (infringements, driverId) => {
    if (!driverId || driverId === 'all') {
        return infringements;
    }
    
    return infringements.filter(infringement => infringement.profile_id === driverId);
};

/**
 * Format date for display
 */
export const formatInfringementDate = (dateString) => {
    try {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch (error) {
        return 'Invalid Date';
    }
};

/**
 * Format date with time for display
 */
export const formatInfringementDateTime = (dateString) => {
    try {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return 'Invalid Date';
    }
};

/**
 * Get severity color for styling
 */
export const getSeverityColor = (severity) => {
    switch (severity) {
        case 'minor': return '#17a2b8';      // Info blue
        case 'serious': return '#ffc107';    // Warning yellow
        case 'very_serious': return '#dc3545'; // Danger red
        default: return '#6c757d';           // Muted gray
    }
};

/**
 * Get status color for styling
 */
export const getStatusColor = (status) => {
    switch (status) {
        case 'pending': return '#ffc107';     // Warning yellow
        case 'acknowledged': return '#17a2b8'; // Info blue
        case 'signed_off': return '#28a745';  // Success green
        case 'disputed': return '#dc3545';    // Danger red
        default: return '#6c757d';           // Muted gray
    }
};

/**
 * Validate infringement form data
 */
export const validateInfringementData = (data) => {
    const errors = {};

    if (!data.profile_id) {
        errors.profile_id = 'Driver is required';
    }

    if (!data.infringement_date) {
        errors.infringement_date = 'Infringement date is required';
    } else {
        const today = new Date();
        const infringementDate = new Date(data.infringement_date);
        if (infringementDate > today) {
            errors.infringement_date = 'Infringement date cannot be in the future';
        }
    }

    if (!data.infringement_type) {
        errors.infringement_type = 'Infringement type is required';
    }

    if (!data.severity) {
        errors.severity = 'Severity is required';
    }

    if (!data.description || data.description.trim().length < 10) {
        errors.description = 'Description is required (minimum 10 characters)';
    }

    if (data.penalty_amount && (isNaN(data.penalty_amount) || parseFloat(data.penalty_amount) < 0)) {
        errors.penalty_amount = 'Penalty amount must be a valid positive number';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

/**
 * Download signed PDF with signature, date, and notes overlaid
 */
const fetchSignedPDF = async (infringementId) => {
    const response = await authFetch(`/api/tacho/${infringementId}/download-signed-pdf`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to download signed PDF ${infringementId}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

    return {
        blob: await response.blob(),
        filename: filenameMatch ? decodeURIComponent(filenameMatch[1]) : `Signed_Infringement_${infringementId}.pdf`
    };
};

const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const downloadSignedPDF = async (infringementId) => {
    try {
        logger.log('📥 Downloading signed PDF for infringement:', infringementId);
        const { blob, filename } = await fetchSignedPDF(infringementId);
        triggerBlobDownload(blob, filename);
        logger.log('✅ Signed PDF downloaded successfully');
        
        return {
            success: true
        };
    } catch (error) {
        logger.error('❌ Error downloading signed PDF:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

export const downloadSignedPDFs = async (infringementIds) => {
    try {
        if (!infringementIds.length) {
            throw new Error('There are no signed infringements to download');
        }

        logger.log('📥 Downloading signed PDFs:', infringementIds);
        const zip = new JSZip();

        for (const infringementId of infringementIds) {
            const { blob, filename } = await fetchSignedPDF(infringementId);
            zip.file(`${infringementId}_${filename}`, blob);
        }

        const archive = await zip.generateAsync({ type: 'blob' });
        const date = new Date().toISOString().slice(0, 10);
        triggerBlobDownload(archive, `Signed_Tacho_Infringements_${date}.zip`);

        logger.log('✅ Signed PDF archive downloaded successfully');
        return { success: true };
    } catch (error) {
        logger.error('❌ Error downloading signed PDF archive:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

export const downloadPrintout = async (infringementId) => {
    try {
        const response = await authFetch(`/api/tacho/${infringementId}/download-printout`);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to download printout');
        }

        // Get the filename from Content-Disposition header or create default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'printout.jpg';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Convert response to blob
        const blob = await response.blob();

        // Create download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return {
            success: true
        };
    } catch (error) {
        logger.error('❌ Error downloading printout:', error);
        return {
            success: false,
            error: error.message
        };
    }
};